import { ErrorBoundary, Icon, Text, HStack, VStack } from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { Box, Center, FlatList, Pressable } from 'native-base';
import React, { useCallback } from 'react';
import { Dimensions, DimensionValue } from 'react-native';

import { BarSelectedItems } from '../../../BarSelectedItems';
import { getParamsDetailsPerProducts, ProductFieldProps } from '../../constants';
import { WithProductId } from '../Mobile';

import { ProductTabletCard } from './components/Card';
import { CardLoading } from './components/CardLoading';
import { ProductItem } from './components/ProductItem';
import { ProductsList } from './components/ProductsList';

export interface ProductTableTabletProps<T extends WithProductId> {
  data: T[];
  isLoading: boolean;
  fields?: ProductFieldProps<T>[];
  columns: number;
  rows?: number;
  selectedItems: string[];
  selectedItemsSet?: Set<string>;
  countSelected: number;
  onSelect: (value: string) => void;
  onDetails: (productId: number, managerImage: string, productName: string) => void;
  onBatchAction: (action: 'block' | 'unblock') => void;
  handleSelecteItems: (value: string) => void;
  isSelectAll: boolean;
  productType: 'funds' | 'fixedIncome';
}

export const ProductTableTablet = <T extends WithProductId>({
  data,
  fields = [],
  isLoading,
  columns,
  rows = 1,
  selectedItems,
  countSelected,
  onSelect,
  onDetails,
  onBatchAction,
  handleSelecteItems,
  isSelectAll,
  productType,
}: ProductTableTabletProps<T>) => {
  const maxHeight = `${Dimensions.get('window').height - 195}px` as DimensionValue;

  const renderDetailsButton = useCallback(
    (item: T) => (
      <Pressable
        testID="button-item"
        onPress={() => {
          const { productId, managerImage, productName } = getParamsDetailsPerProducts(
            item,
            productType
          );
          onDetails(productId, managerImage, productName);
        }}
        flex={1 / columns}>
        <HStack alignItems="center" space={Spacing['inline.nano']}>
          <Text
            color={'brand.tertiary.principal'}
            variant="semibold"
            fontSize={Typography['fontsize.md']}>
            DETALHES
          </Text>
          <Icon name="ChevronRight" size={'xxxs'} color={Color['brand.tertiary.principal']} />
        </HStack>
      </Pressable>
    ),
    [columns]
  );

  const renderItem = useCallback(
    (label: string, value: unknown, item: T) => (
      <ProductItem
        columns={columns}
        isLoading={isLoading}
        label={label}
        rows={rows}
        value={value}
        onSelect={onSelect}
        selectedItems={selectedItems}
        productId={item.productId.toString()}
        managerImage={item.managerImage}
      />
    ),
    [columns, isLoading, rows, onSelect, selectedItems]
  );

  const createFieldsWithActions = useCallback(
    (item: T) => {
      const detailsField = {
        label: '',
        key: 'details' as keyof T,
        render: () => renderDetailsButton(item),
      };

      return [...fields, detailsField];
    },
    [fields, renderDetailsButton]
  );

  const renderProductItem = useCallback(
    (item: T, index: number) => {
      const fieldsWithActions = createFieldsWithActions(item);
      return (
        <ProductTabletCard index={index} key={item.productId.toString()}>
          <ProductsList
            id="flatList-dynamicTable-detail"
            keyExtractor={(field) => `${field.label}`}
            data={fieldsWithActions}
            renderItem={({ item: field }) => {
              if ('render' in field && field.render && typeof field.render === 'function') {
                return (
                  <Box
                    w={rows !== 1 ? `${100 / columns}%` : undefined}
                    flex={1 / columns}
                    justifyContent="center"
                    p={Spacing['inline.xxxs']}>
                    {field.render()}
                  </Box>
                );
              }
              return renderItem(field.label, item[field.key], item);
            }}
            columns={columns}
          />
        </ProductTabletCard>
      );
    },
    [createFieldsWithActions, renderItem, rows, columns]
  );

  if (isLoading) {
    return Array.from({ length: 5 }).map((_, index) => <CardLoading key={index} />);
  }

  if (data?.length === 0 && !isLoading) {
    return (
      <Center h={'400px'}>
        <ErrorBoundary
          errorIconOrIllustration="NotFoundIllustration"
          errorTitle="Nenhum resultado corresponde à sua pesquisa"
          errorMessage="Por favor, verifique o campo informado e tente novamente."
        />
      </Center>
    );
  }

  return (
    <VStack space={Spacing['stack.xxxs']} pb={Spacing['stack.xxxs']} overflow={'hidden'}>
      {countSelected > 1 && (
        <BarSelectedItems
          countSelected={countSelected}
          onBatchAction={onBatchAction}
          handleSelecteItems={handleSelecteItems}
          isSelectAll={isSelectAll}
        />
      )}
      <FlatList
        windowSize={10}
        maxToRenderPerBatch={10}
        updateCellsBatchingPeriod={50}
        initialNumToRender={10}
        removeClippedSubviews={true}
        data={data}
        contentContainerStyle={{ maxHeight, padding: parseInt(Spacing['stack.nano']) }}
        showsVerticalScrollIndicator={false}
        onEndReachedThreshold={0.5}
        ItemSeparatorComponent={() => <Box height={Spacing['stack.nano']} />}
        renderItem={({ item, index }) => renderProductItem(item, index)}
        keyExtractor={(item, index) => `${item.productId}-${index}`}
        getItemLayout={(_, index) => ({
          length: 150,
          offset: 150 * index,
          index,
        })}
      />
    </VStack>
  );
};
